import api from './api';

/** Instrumentos de carregamento que o motor de preços conhece. */
export const TOPUP_INSTRUMENTS = ['VINTI4', 'VISA', 'MASTERCARD', 'CASH', 'BANK_TRANSFER'] as const;
export type TopUpInstrument = typeof TOPUP_INSTRUMENTS[number];

export interface CashExample {
  entregue: number;
  taxa: number;
  creditado: number;
}

export interface TopUpRates {
  contractId: string;
  promoterEntityId: string;
  version: number;
  effectiveFrom: string;
  bearer: 'PARTICIPANT' | 'PROMOTER';
  defaultRateBps: number;
  minTopUpCVE: number;
  maxTopUpCVE: number;
  vatRateBps: number;
  vatTopUpMode: 'INCLUSIVE' | 'EXCLUSIVE';
  ratesBps: Record<string, number | null>;
  exemploNumerario: CashExample | null;
}

export interface TopUpRatesVersion {
  contractId: string;
  version: number;
  status: 'DRAFT' | 'ACTIVE' | 'SUPERSEDED' | 'TERMINATED';
  effectiveFrom: string;
  createdBy: string;
  createdAt: string;
  defaultRateBps: number;
  minTopUpCVE: number;
  maxTopUpCVE: number;
  ratesBps: Record<string, number>;
}

export interface PublishRatesPayload {
  ratesBps: Partial<Record<TopUpInstrument, number>>;
  defaultRateBps?: number;
  minTopUpCVE?: number;
  maxTopUpCVE?: number;
  motivo?: string;
}

export interface PublishRatesResult {
  contractId: string;
  version: number;
  anterior: { contractId: string; version: number };
  ratesBps: Record<string, number>;
  minTopUpCVE: number;
  maxTopUpCVE: number;
  motivo: string | null;
  exemploNumerario: CashExample | null;
}

/**
 * Taxas de carregamento da carteira.
 *
 * Os contratos são imutáveis: publicar taxas novas cria uma versão e retira a
 * anterior de circulação. As cotações já emitidas continuam a explicar-se pela
 * versão com que foram calculadas.
 */
export interface CashLimit {
  id: number;
  escopo: 'CLIENTE' | 'OPERADOR';
  kycLevel: 'BASIC' | 'STANDARD' | 'ENHANCED' | null;
  perTxMaxCVE: number;
  dailyMaxCVE: number;
  monthlyMaxCVE: number;
  dailyCountMax: number;
  structuringThresholdCVE: number;
  structuringWindowHours: number;
  structuringMinCount: number;
  ativo: boolean;
  updatedBy: string | null;
  updatedAt: string;
}

export type CashLimitPatch = Partial<Omit<CashLimit, 'id' | 'escopo' | 'kycLevel' | 'updatedBy' | 'updatedAt'>>;

export interface CashTopupFilters {
  from?: string;
  to?: string;
  operador?: string;
  agente?: string;
  terminal?: string;
  origem?: 'WALLET_POS' | 'WRISTBAND';
  /** 'so' = apenas estornados, 'nao' = apenas válidos */
  estornados?: 'so' | 'nao';
  page?: number;
  limit?: number;
}

export interface CashTopupRow {
  referencia: string;
  origem: 'WALLET_POS' | 'WRISTBAND';
  beneficiarioUserId: string | null;
  walletId: string | null;
  operadorUserId: string | null;
  terminalId: string | null;
  agenteId: string | null;
  eventoId: string | null;
  recebido: number;
  taxa: number;
  creditado: number;
  rateBps: number;
  financiamento: string;
  criadoEm: string;
  estornadoEm: string | null;
  estornoMotivo: string | null;
  estornadoPor: string | null;
  portadorETitular: boolean | null;
  portadorNome: string | null;
  portadorDoc: string | null;
  portadorNif: string | null;
}

export interface CashTopupTotais {
  operacoes: number;
  recebido: number;
  taxa: number;
  creditado: number;
  aLiquidar: number;
  estornados: number;
  porTerceiros: number;
}

export interface CashTopupsResult {
  linhas: CashTopupRow[];
  totais: CashTopupTotais;
  pagination: { page: number; limit: number; from: string; to: string | null };
}

export const topupRatesService = {
  async get(): Promise<TopUpRates> {
    const { data } = await api.get('/pass/topup-rates');
    return data.data;
  },

  async publish(payload: PublishRatesPayload): Promise<PublishRatesResult> {
    const { data } = await api.put('/pass/topup-rates', payload);
    return data.data;
  },

  async history(): Promise<TopUpRatesVersion[]> {
    const { data } = await api.get('/pass/topup-rates/history');
    return data.data;
  },

  /** Registo de carregamentos em numerário — carteira ao balcão e pulseiras. */
  async cashTopups(filtros: CashTopupFilters = {}): Promise<CashTopupsResult> {
    const { data } = await api.get('/pass/cash-topups', { params: filtros });
    return { linhas: data.data, totais: data.totais, pagination: data.pagination };
  },

  /** Limites de numerário. Vivem à parte das taxas: são controlo de risco, não termos contratuais. */
  async limits(): Promise<CashLimit[]> {
    const { data } = await api.get('/pass/cash-limits');
    return data.data;
  },

  async updateLimit(id: number, patch: CashLimitPatch): Promise<void> {
    await api.put(`/pass/cash-limits/${id}`, patch);
  },
};

export default topupRatesService;
